// src/pages/Appli/AppliAdmin/TestimonialsAdmin/TestimonialsAdmin.jsx
import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";

import {
  getAllTestimonials,
  deleteTestimonial,
} from "./../../../../services/testimonialServices";

import ButtonEdit from "./../../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../../components/Button/ButtonDelete";
import CreateTestimonialModal from "./CreateTestimonialModal";
import EditTestimonialModal from "./EditTestimonialModal";

import { useNotification } from "./../../../../components/Notification/NotificationManager";
import { LanguageContext } from "./../../../../contexts/LanguageContext";

import {
  FaPlus,
  FaSearch,
  FaSortAmountDownAlt,
  FaSortAmountUpAlt,
  FaFilter,
} from "react-icons/fa";

const sanitize = (html) => DOMPurify.sanitize(String(html || ""));

const stripHtml = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = String(html || "");
  return (div.textContent || div.innerText || "").trim();
};

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("fr")) return "fr";
  if (v.startsWith("en")) return "en";
  return "fr";
};

const fmtDate = (d) => {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(d));
  } catch {
    return "—";
  }
};

export default function TestimonialsAdmin() {
  const { addNotification } = useNotification();
  const { language } = useContext(LanguageContext);

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(null);

  const [localLanguage, setLocalLanguage] = useState(() => normLang(language));

  // UI controls
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [sortBy, setSortBy] = useState("ordre"); // "ordre" | "date"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  useEffect(() => {
    setLocalLanguage(normLang(language));
  }, [language]);

  const fetchTestimonials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllTestimonials();
      setTestimonials(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des témoignages :", error);
      addNotification("Erreur lors de la récupération des témoignages.", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleDelete = async (id) => {
    try {
      await deleteTestimonial(id);
      addNotification("Témoignage supprimé avec succès.", "success");
      fetchTestimonials();
    } catch (error) {
      console.error("Erreur lors de la suppression du témoignage :", error);
      addNotification("Erreur lors de la suppression du témoignage.", "error");
    }
  };

  const titleKey = useMemo(
    () => `testimonial_title_${normLang(localLanguage)}`,
    [localLanguage]
  );
  const contentKey = useMemo(
    () => `testimonial_content_${normLang(localLanguage)}`,
    [localLanguage]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = [...testimonials];

    if (activeOnly) {
      arr = arr.filter((t) => !!t.is_active);
    }

    if (q) {
      arr = arr.filter((t) => {
        const title = String(t?.[titleKey] || "").toLowerCase();
        const content = stripHtml(t?.[contentKey] || "").toLowerCase();
        const ordre = String(t?.ordre ?? "");
        const date = String(t?.testimonial_date ?? "");
        return (
          title.includes(q) ||
          content.includes(q) ||
          ordre.includes(q) ||
          date.includes(q)
        );
      });
    }

    arr.sort((a, b) => {
      if (sortBy === "date") {
        const ta = new Date(a?.testimonial_date || 0).getTime();
        const tb = new Date(b?.testimonial_date || 0).getTime();
        return sortDir === "asc" ? ta - tb : tb - ta;
      }
      // ordre
      const oa = Number(a?.ordre ?? 0);
      const ob = Number(b?.ordre ?? 0);
      return sortDir === "asc" ? oa - ob : ob - oa;
    });

    return arr;
  }, [testimonials, query, activeOnly, sortBy, sortDir, titleKey, contentKey]);

  const ui = useMemo(() => {
    const wrap = "max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-700 dark:text-gray-300";

    const card =
      "rounded-3xl border border-black/10 bg-white/70 shadow-sm backdrop-blur-sm " +
      "dark:bg-white/5 dark:border-white/10 dark:shadow-none";

    const input =
      "w-full rounded-2xl px-3 py-2 text-sm outline-none transition " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "focus:ring-2 focus:ring-primary focus:border-transparent";

    const chip =
      "inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 " +
      "text-gray-900 dark:text-white " +
      "hover:bg-white dark:hover:bg-black/30 transition";

    const btnPrimary =
      "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2 text-sm font-extrabold " +
      "bg-primary text-white shadow hover:opacity-95 active:scale-[0.99] transition";

    return { wrap, title, muted, card, input, chip, btnPrimary };
  }, []);

  return (
    <div className="relative min-h-screen">
      {/* fond léger */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent dark:from-black/70" />
      </div>

      <div className={`${ui.wrap} relative z-10 pt-10 pb-10`}>
        {/* Header */}
        <div className={`${ui.card} p-5 sm:p-6`}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className={`text-2xl sm:text-3xl font-extrabold ${ui.title}`}>
                  Gestion des Témoignages
                </h1>
                <p className={`mt-1 text-sm ${ui.muted}`}>
                  Crée, édite et organise tes témoignages (langue, ordre, date, actif).
                </p>
              </div>

              <button
                type="button"
                className={ui.btnPrimary}
                onClick={() => setIsCreateModalOpen(true)}
              >
                <FaPlus />
                Créer
              </button>
            </div>

            {/* Toolbar */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              {/* Search */}
              <div className="lg:col-span-5">
                <label className={`text-xs font-semibold ${ui.muted} mb-1 flex items-center gap-2`}>
                  <FaSearch className="text-primary" /> Recherche
                </label>
                <input
                  className={ui.input}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Titre, contenu, ordre, date…"
                />
              </div>

              {/* Language */}
              <div className="lg:col-span-2">
                <label className={`text-xs font-semibold ${ui.muted} mb-1 flex items-center gap-2`}>
                  🌍 Langue
                </label>
                <select
                  className={ui.input}
                  value={localLanguage}
                  onChange={(e) => setLocalLanguage(normLang(e.target.value))}
                >
                  <option value="fr">FR</option>
                  <option value="en">EN</option>
                </select>
              </div>

              {/* Sort */}
              <div className="lg:col-span-3">
                <label className={`text-xs font-semibold ${ui.muted} mb-1 flex items-center gap-2`}>
                  <FaFilter className="text-primary" /> Tri
                </label>
                <div className="flex gap-2">
                  <select
                    className={ui.input}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="ordre">Ordre</option>
                    <option value="date">Date</option>
                  </select>

                  <button
                    type="button"
                    className={ui.chip}
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    title="Inverser"
                  >
                    {sortDir === "asc" ? <FaSortAmountUpAlt className="text-primary" /> : <FaSortAmountDownAlt className="text-primary" />}
                    {sortDir.toUpperCase()}
                  </button>
                </div>
              </div>

              {/* Active only */}
              <div className="lg:col-span-2 flex items-end">
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-primary"
                    checked={activeOnly}
                    onChange={(e) => setActiveOnly(e.target.checked)}
                  />
                  <span className={`text-sm font-semibold ${ui.title}`}>
                    Actifs seulement
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-6">
          {loading ? (
            <div className={`${ui.card} p-6`}>
              <p className={`text-center ${ui.muted}`}>Chargement…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`${ui.card} p-6`}>
              <p className={`text-center ${ui.muted}`}>Aucun témoignage.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map((t) => {
                const title = t?.[titleKey] || "Titre indisponible";
                const content = t?.[contentKey] || "<p>Contenu indisponible</p>";
                const isActive = !!t?.is_active;

                return (
                  <div key={t.id} className={`${ui.card} p-5 sm:p-6 flex flex-col gap-4`}>
                    {/* Top meta */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`text-xs font-semibold ${ui.muted}`}>
                          Ordre : <span className="font-extrabold text-primary">{t.ordre ?? "—"}</span>
                          <span className="mx-2">•</span>
                          Date : <span className="font-semibold">{fmtDate(t.testimonial_date)}</span>
                        </div>

                        <h3 className={`mt-2 text-lg sm:text-xl font-extrabold ${ui.title} break-words`}>
                          {title}
                        </h3>
                      </div>

                      <span
                        className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-extrabold border ${
                          isActive
                            ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
                            : "border-gray-400/30 bg-black/5 text-gray-700 dark:text-gray-300 dark:bg-white/5"
                        }`}
                      >
                        {isActive ? "Actif" : "Inactif"}
                      </span>
                    </div>

                    {/* Quill-rendered preview */}
                    <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/20 p-4 relative overflow-hidden">
                      <div className="ql-snow">
                        <div
                          className={`ql-editor p-0 text-sm leading-relaxed ${ui.muted} dark:text-gray-200`}
                          style={{ maxHeight: 180, overflow: "hidden" }}
                          dangerouslySetInnerHTML={{ __html: sanitize(content) }}
                        />
                      </div>
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white/90 to-transparent dark:from-slate-900/90" />
                    </div>

                    {/* Actions */}
                    <div className="mt-auto flex justify-end gap-2">
                      <ButtonEdit
                        onClick={() =>
                          setEditModalData({
                            ...t,
                            language: localLanguage,
                          })
                        }
                      />
                      <ButtonDelete onClick={() => handleDelete(t.id)} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modales */}
        {isCreateModalOpen && (
          <CreateTestimonialModal
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={() => fetchTestimonials()}
            language={localLanguage}
          />
        )}

        {editModalData && (
          <EditTestimonialModal
            testimonial={editModalData}
            onClose={() => setEditModalData(null)}
            onUpdate={() => fetchTestimonials()}
            language={localLanguage}
          />
        )}
      </div>
    </div>
  );
}
